import React from "react";
import { Link } from "react-router-dom";
import MarketingShell from "../MarketingShell";
import SEOHead from "../../components/SEOHead";
import { useOpeningHours, openingHoursSchema, UpcomingChanges } from "../useBusinessInfo";
import "./free-museum.css";

/**
 * Redesigned Free Museum page (route: /free-museum).
 *
 * Visual ground truth: design-ref/public/Free Museum.html
 * Functional ground truth: frontend/src/pages/FreeMuseumPage.js
 *  - live opening hours via useOpeningHours (GET /api/opening-hours), incl.
 *    closed days and upcoming special days
 *  - hero video /museum-hero.mp4 (poster + img fallback)
 *  - SEOHead with Museum JSON-LD (isAccessibleForFree: true)
 */
const RFreeMuseumPage = () => {
  const hours = useOpeningHours();

  // Card title is the shared open-day hours when every open day matches
  // (e.g. "8:30am – 5:30pm"); the text keeps the rest of the summary
  // ("Open daily." / "Open. Closed Tuesdays.").
  const openTexts = [...new Set(hours.weekly.filter((d) => !d.closed).map((d) => d.text))];
  const uniformHours = openTexts.length === 1 ? openTexts[0] : null;
  const hoursTitle = uniformHours || "Hours vary by day";
  const hoursText = hours.summary
    .map((line) => (uniformHours ? line.replace(` ${uniformHours}`, "") : line))
    .join(". ");

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Museum",
    "name": "Umoona Opal Mine Free Underground Museum",
    "description": "Visit our free opal mining museum in Coober Pedy. Explore authentic mining history, opal displays, underground accommodation and learn about outback life.",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "14 Hutchison St",
      "addressLocality": "Coober Pedy",
      "addressRegion": "SA",
      "postalCode": "5723",
      "addressCountry": "AU"
    },
    ...openingHoursSchema(hours),
    "isAccessibleForFree": true,
    "url": "https://umoonaopalmine.com.au/free-museum"
  };

  const discover = [
    {
      kicker: "HISTORY",
      kickerClass: "",
      title: "Story of Coober Pedy",
      text: "Learn about its people and the unique underground lifestyle.",
    },
    {
      kicker: "FOSSILS",
      kickerClass: " fm-card__kicker--teal",
      title: "Fossil displays",
      text: "Extensive fossil displays and geological specimens.",
    },
    {
      kicker: "GEOLOGY",
      kickerClass: " fm-card__kicker--turquoise",
      title: "Opal formation",
      text: "Comprehensive exhibits explaining how opals are formed.",
    },
    {
      kicker: "CINEMA",
      kickerClass: " fm-card__kicker--blue",
      title: "Underground cinema",
      text: 'The award-winning "Story of Opal" documentary.',
    },
    {
      kicker: "HERITAGE",
      kickerClass: "",
      title: "Mining equipment",
      text: "Original mining equipment displays from the 1920s.",
    },
    {
      kicker: "CULTURE",
      kickerClass: " fm-card__kicker--teal",
      title: "Aboriginal culture",
      text: "Traditional Aboriginal cultural artifacts.",
    },
  ];

  const highlights = [
    {
      kicker: "20 MINUTES",
      kickerClass: "",
      title: "Story of Opal Cinema",
      text: "Award-winning documentary presented in an underground theatre on three panoramic screens.",
    },
    {
      kicker: "1920s",
      kickerClass: " fm-card__kicker--teal",
      title: "Mining heritage",
      text: "Authentic mining history with original equipment and techniques.",
    },
    {
      kicker: "OPALISED",
      kickerClass: " fm-card__kicker--turquoise",
      title: "Fossil collection",
      text: "Unique opalised fossils and geological specimens found in the region.",
    },
  ];

  return (
    <MarketingShell pageClass="pg-free-museum">
      <SEOHead
        title="Free Opal Mining Museum - Umoona Opal Mine & Museum"
        description="Visit our free opal mining museum in Coober Pedy. Explore authentic mining history, opal displays, underground accommodation and learn about outback life."
        keywords="free museum, opal museum, Coober Pedy museum, mining history, underground museum, South Australia attractions"
        canonical="/free-museum"
        schemaData={schemaData}
      />

      {/* HERO — design layout, with the old page's background video kept
          (poster/fallback = the design's museum-1.jpg still) */}
      <div className="u-hero">
        <video
          className="u-hero__img"
          autoPlay
          loop
          muted
          playsInline
          poster="/design/assets/museum-1.jpg"
        >
          <source src="/museum-hero.mp4" type="video/mp4" />
          <img
            src="/design/assets/museum-1.jpg"
            alt="Umoona Underground Museum"
            className="u-hero__img"
          />
        </video>
        <div className="u-hero__scrim" />
        <div className="u-hero__inner">
          <div className="u-hero__eyebrow">Completely FREE entry · Underground cinema</div>
          <h1 className="u-hero__title">Free Underground Museum Experience</h1>
          <p className="u-hero__sub">
            A beautiful museum telling the history of the area, its people, fossils and opals — with
            an underground cinema showing our award-winning film. Free to explore, for all ages.
          </p>
        </div>
      </div>

      {/* WHAT YOU'LL DISCOVER (6) */}
      <section className="u-sectpad u-sect-deep">
        <div className="u-eyebrow">What you'll discover</div>
        <h2 className="u-h2">A whole world beneath Coober Pedy</h2>
        <div className="u-grid3">
          {discover.map((item) => (
            <div className="fm-card" key={item.title}>
              <div className={"fm-card__kicker" + item.kickerClass}>{item.kicker}</div>
              <h3 className="fm-card__title">{item.title}</h3>
              <p className="fm-card__text">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MUSEUM HIGHLIGHTS (3) */}
      <section className="u-sectpad u-sect-test">
        <div className="u-eyebrow">Museum highlights</div>
        <h2 className="u-h2">Three things not to miss</h2>
        <div className="u-grid3">
          {highlights.map((item) => (
            <div className="fm-card" key={item.title}>
              <div className={"fm-card__kicker" + item.kickerClass}>{item.kicker}</div>
              <h3 className="fm-card__title">{item.title}</h3>
              <p className="fm-card__text">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* VISITOR INFORMATION */}
      <section className="u-sectpad u-sect-deep">
        <div className="u-grid3">
          <div className="fm-card">
            <div className="fm-card__kicker">OPENING HOURS</div>
            <h3 className="fm-card__title">{hoursTitle}</h3>
            <p className="fm-card__text">
              {`${hoursText}. Free admission, all ages welcome.`}
            </p>
            <UpcomingChanges specialDays={hours.special_days} className="fm-card__text" inline />
          </div>
          <div className="fm-card">
            <div className="fm-card__kicker fm-card__kicker--teal">ADMISSION</div>
            <h3 className="fm-card__title">FREE entry</h3>
            <p className="fm-card__text">All ages welcome. Wheelchair-accessible areas available.</p>
          </div>
          <div className="fm-card">
            <div className="fm-card__kicker fm-card__kicker--turquoise">LOCATION</div>
            <h3 className="fm-card__title">14 Hutchison St</h3>
            <p className="fm-card__text">Coober Pedy SA 5723 · Phone (08) 8672 5288</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="u-sectpad u-sect-cta">
        <div className="u-ctacenter">
          <h2 className="u-ctacenter__title">Ready to explore underground?</h2>
          <p className="u-ctacenter__sub">
            Visit our free museum and discover the world beneath Coober Pedy. Add a guided tour for
            the complete experience!
          </p>
          <div className="u-ctacenter__btns">
            <Link to="/contact" className="u-btn u-btn--primary">Get Directions</Link>
            <Link to="/underground-tours" className="u-btn u-btn--ghost">Book Guided Tour</Link>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
};

export default RFreeMuseumPage;
